import React, { useMemo } from 'react';
import {
  Box, Chip, Stack, Typography, Select, MenuItem,
  FormControl, IconButton, Collapse,
} from '@mui/material';
import { FilterList as FilterListIcon, Close as CloseIcon } from '@mui/icons-material';

export default function FilterBar({
  taxonomy = {},
  filters = {},
  onChange,
  sortValue,
  onSortChange,
  totalCount,
  filteredCount,
}) {
  const [open, setOpen] = React.useState(true);

  const { levels = [], products = [], roles = [] } = taxonomy;

  const topProducts = useMemo(() =>
    products.flatMap((p) => [p, ...(p.children || [])]).slice(0, 20),
    [products]
  );

  const toggle = (key, value) => {
    const current = filters[key] || [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onChange({ ...filters, [key]: next });
  };

  const isActive = (key, value) => (filters[key] || []).includes(value);

  const clearAll = () => onChange({ levels: [], products: [], roles: [] });

  const hasFilters = Object.values(filters).some((v) => v?.length > 0);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Filter header row */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <IconButton size="small" onClick={() => setOpen((o) => !o)}>
          <FilterListIcon fontSize="small" />
        </IconButton>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {filteredCount !== undefined && totalCount !== undefined
            ? `${filteredCount.toLocaleString()} of ${totalCount.toLocaleString()} results`
            : ''}
        </Typography>
        {hasFilters && (
          <Chip
            label="Clear all"
            size="small"
            onDelete={clearAll}
            deleteIcon={<CloseIcon />}
            sx={{ fontSize: '0.72rem' }}
          />
        )}
        {onSortChange && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <Select
              value={sortValue || 'popularity'}
              onChange={(e) => onSortChange(e.target.value)}
              displayEmpty
              sx={{ fontSize: '0.8rem', borderRadius: '8px' }}
            >
              <MenuItem value="popularity">Most Popular</MenuItem>
              <MenuItem value="rating">Highest Rated</MenuItem>
              <MenuItem value="newest">Newest</MenuItem>
              <MenuItem value="duration_asc">Shortest First</MenuItem>
              <MenuItem value="duration_desc">Longest First</MenuItem>
              <MenuItem value="title">A–Z</MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>

      <Collapse in={open}>
        {/* Level filters */}
        {levels.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.75 }}>
              Level
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
              {levels.map((l) => (
                <Chip
                  key={l.id}
                  label={l.name}
                  size="small"
                  clickable
                  onClick={() => toggle('levels', l.id)}
                  variant={isActive('levels', l.id) ? 'filled' : 'outlined'}
                  color={isActive('levels', l.id) ? 'primary' : 'default'}
                  sx={{ fontSize: '0.75rem', borderRadius: '6px' }}
                />
              ))}
            </Stack>
          </Box>
        )}

        {/* Product filters */}
        {topProducts.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.75 }}>
              Product
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 0.75,
                flexWrap: 'wrap',
                maxHeight: 80,
                overflow: 'hidden',
                '&:hover': { maxHeight: 'none' },
              }}
            >
              {topProducts.map((p) => (
                <Chip
                  key={p.id}
                  label={p.name}
                  size="small"
                  clickable
                  onClick={() => toggle('products', p.id)}
                  variant={isActive('products', p.id) ? 'filled' : 'outlined'}
                  color={isActive('products', p.id) ? 'primary' : 'default'}
                  sx={{ fontSize: '0.75rem', borderRadius: '6px' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Role filters */}
        {roles.length > 0 && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', mb: 0.75 }}>
              Role
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
              {roles.slice(0, 12).map((r) => (
                <Chip
                  key={r.id}
                  label={r.name}
                  size="small"
                  clickable
                  onClick={() => toggle('roles', r.id)}
                  variant={isActive('roles', r.id) ? 'filled' : 'outlined'}
                  color={isActive('roles', r.id) ? 'primary' : 'default'}
                  sx={{ fontSize: '0.75rem', borderRadius: '6px' }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}
